/**
 * Live session top bar — session title, timers, and topic progress.
 */

import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  CircleHelp,
  Clock,
  Target,
  Users,
  UserX,
  Wifi,
} from "lucide-react";
import type { SessionTopicPlan, TopicSegmentView } from "./topicProgress/types";
import { parseEndTimeToMinutes } from "./topicProgress/sessionEndTime";
import {
  buildTopicSegments,
  getTopicStatusLabel,
  getSegmentStatusColors,
} from "./topicProgress/topicSegments";

function TopicSegmentTooltip({
  anchorEl,
  segment,
}: {
  anchorEl: HTMLElement;
  segment: TopicSegmentView;
}) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const place = () => {
      const r = anchorEl.getBoundingClientRect();
      setPos({
        top: r.bottom + 8,
        left: r.left + r.width / 2,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorEl, segment.id]);

  const statusColor = getSegmentStatusColors(segment.status).label;

  return createPortal(
    <div
      role="tooltip"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        transform: "translateX(-50%)",
        zIndex: 10000,
        width: "max-content",
        minWidth: 160,
        maxWidth: 220,
        borderRadius: 8,
        background: "#0f172a",
        border: "1px solid rgba(148,163,184,0.25)",
        padding: "10px 12px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
        pointerEvents: "none",
      }}
    >
      {segment.parentTopicName && (
        <p
          style={{
            margin: 0,
            fontSize: 10,
            color: "#94a3b8",
            fontWeight: 500,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
          }}
        >
          {segment.parentTopicName}
        </p>
      )}
      <p
        style={{
          margin: 0,
          fontSize: 13,
          fontWeight: 600,
          color: "#fff",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          wordBreak: "break-word",
        }}
      >
        {segment.name}
      </p>
      <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>
        {segment.status === "pending" ? "Expected time" : "Time taken"}:{" "}
        <span style={{ fontWeight: 600, color: "#fff" }}>
          {segment.status === "pending"
            ? segment.expectedTime
            : segment.timeTaken}
        </span>
      </p>
      <p
        style={{
          margin: "4px 0 0",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: statusColor,
        }}
      >
        {getTopicStatusLabel(segment.status)}
      </p>
    </div>,
    document.body,
  );
}

export type LiveKitTopBarProps = {
  homePath: string;
  sessionTitle: string;
  role: "trainer" | "student";
  participantCount: number;
  /** Invited students not currently present (trainer). */
  absenteeCount?: number;
  topics?: SessionTopicPlan[];
  currentTopic?: SessionTopicPlan;
  topicProgressPercent?: number;
  topicElapsedLabel?: string;
  sessionTimer?: string;
  /** Original scheduled end — trainer only. */
  scheduledEndTime?: string;
  /** Live projected end — trainer only in our student-trimmed UI. */
  expectedEndTime?: string;
  connectionLabel?: string;
  /** Color hint for connection strength. */
  connectionTone?: "excellent" | "good" | "poor" | "lost" | "offline" | "unknown";
  recording?: boolean;
  /** Logical recording paused (egress segment stopped; session still open). */
  recordingPaused?: boolean;
  doubtsCount?: number;
  feedbackCount?: number;
  displayName?: string;
  avatarUrl?: string;
  mediaError?: string | null;
  onOpenAbsorption?: () => void;
  /** Trainer: open Attendance & Engagement → Attendees. */
  onOpenAttendees?: () => void;
  /** Trainer: open Attendance & Engagement → Absentees. */
  onOpenAbsentees?: () => void;
  /** Narrow viewport: stacked header + second-row topic progress. */
  compact?: boolean;
  /** True after trainer clicks Start topic — drives LIVE badge + segment bar. */
  topicStarted?: boolean;
  /** Active subtopic id when topic has nested subtopics. */
  currentSubtopicId?: string | null;
  /** Display name for the active subtopic (top-bar label). */
  currentSubtopicName?: string | null;
  /** Prefer subtopic progress % for the active segment fill. */
  subtopicProgressPercent?: number;
  subtopicElapsedLabel?: string;
};

function TopicSegmentBar({
  topics,
  currentTopic,
  topicProgressPercent,
  topicElapsedLabel,
  teachingStarted = false,
  currentSubtopicId,
  compact = false,
}: {
  topics: SessionTopicPlan[];
  currentTopic?: SessionTopicPlan;
  topicProgressPercent: number;
  topicElapsedLabel: string;
  teachingStarted?: boolean;
  currentSubtopicId?: string | null;
  compact?: boolean;
}) {
  const SEGMENT_MIN_WIDTH_PX = 28;
  const [hover, setHover] = useState<{
    id: string;
    el: HTMLElement;
  } | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!teachingStarted) return;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [teachingStarted]);
  const segments = useMemo(
    () =>
      buildTopicSegments(
        topics,
        currentTopic,
        topicProgressPercent,
        topicElapsedLabel,
        teachingStarted,
        currentSubtopicId,
        nowMs,
      ),
    [
      topics,
      currentTopic,
      topicProgressPercent,
      topicElapsedLabel,
      teachingStarted,
      currentSubtopicId,
      nowMs,
    ],
  );
  const hoveredSegment = hover
    ? segments.find((s) => s.id === hover.id) ?? null
    : null;

  const trackStyle: React.CSSProperties = compact
    ? {
        position: "relative",
        display: "flex",
        gap: 4,
        flex: 1,
        minWidth: 64,
        maxWidth: "100%",
        alignItems: "center",
        alignSelf: "center",
        overflowX: segments.length > 5 ? "auto" : "visible",
        overflowY: "visible",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }
    : {
        position: "relative",
        display: "flex",
        gap: 6,
        flex: "1 1 280px",
        minWidth: 120,
        maxWidth: "100%",
        alignItems: "center",
        overflowX: segments.length > 5 ? "auto" : "visible",
        overflowY: "visible",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      };

  if (segments.length === 0) {
    return (
      <div
        style={{
          ...trackStyle,
          height: 10,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 10,
              borderRadius: 999,
              background: i === 0 ? "#3b82f6" : "rgba(148,163,184,0.2)",
              border: i === 0 ? undefined : "2px solid rgba(255,255,255,0.2)",
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={trackStyle}>
      {segments.map((segment) => {
        const colors = getSegmentStatusColors(segment.status);
        return (
        <div
          key={segment.id}
          style={{
            position: "relative",
            flex: `1 0 ${SEGMENT_MIN_WIDTH_PX}px`,
            minWidth: SEGMENT_MIN_WIDTH_PX,
            overflow: "visible",
          }}
          onMouseEnter={(e) =>
            setHover({ id: segment.id, el: e.currentTarget })
          }
          onMouseLeave={() => setHover(null)}
          role="button"
          tabIndex={0}
          aria-label={`${segment.name}, ${getTopicStatusLabel(segment.status)}`}
        >
          <div
            style={{
              overflow: "hidden",
              borderRadius: 999,
              height: 10,
              background: colors.track,
              border: colors.border,
              boxSizing: "border-box",
            }}
            role="progressbar"
            aria-valuenow={segment.fillPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {segment.status === "current" ||
            segment.status === "completed" ||
            segment.status === "skipped" ? (
              <div
                style={{
                  height: "100%",
                  borderRadius: 999,
                  width: `${
                    segment.status === "current"
                      ? Math.max(0, Math.min(100, segment.fillPercent))
                      : 100
                  }%`,
                  minWidth:
                    segment.status === "current" && segment.fillPercent <= 0
                      ? 6
                      : undefined,
                  background: colors.fill,
                  transition: "width 0.3s ease",
                }}
              />
            ) : null}
          </div>
        </div>
        );
      })}
      {hover && hoveredSegment ? (
        <TopicSegmentTooltip anchorEl={hover.el} segment={hoveredSegment} />
      ) : null}
    </div>
  );
}

export function LiveKitTopBar({
  homePath,
  sessionTitle,
  role,
  participantCount,
  absenteeCount,
  topics = [],
  currentTopic,
  topicProgressPercent = 0,
  topicElapsedLabel = "0:00",
  sessionTimer,
  scheduledEndTime,
  expectedEndTime,
  connectionLabel = "Offline",
  connectionTone = "offline",
  recording = false,
  recordingPaused = false,
  doubtsCount = 0,
  feedbackCount = 0,
  displayName = "",
  avatarUrl,
  mediaError,
  onOpenAbsorption,
  onOpenAttendees,
  onOpenAbsentees,
  compact = false,
  topicStarted = false,
  currentSubtopicId = null,
  subtopicProgressPercent,
  subtopicElapsedLabel,
}: LiveKitTopBarProps) {
  const isTrainer = role === "trainer";
  const resolvedCurrent =
    currentTopic ?? topics.find((t) => !t.completed) ?? topics[0];
  const segmentProgressPercent =
    currentSubtopicId && subtopicProgressPercent != null
      ? subtopicProgressPercent
      : topicProgressPercent;
  const segmentElapsedLabel =
    currentSubtopicId && subtopicElapsedLabel
      ? subtopicElapsedLabel
      : topicElapsedLabel;
  const flatSegments = useMemo(() => {
    const flat: Array<{ completed: boolean }> = [];
    for (const t of topics) {
      if (t.subtopics && t.subtopics.length > 0) {
        for (const s of t.subtopics) {
          flat.push({ completed: Boolean(s.completed || s.skipped) });
        }
      } else {
        flat.push({ completed: Boolean(t.completed || t.skipped) });
      }
    }
    return flat;
  }, [topics]);
  const completedCount = flatSegments.filter((s) => s.completed).length;
  const totalSegments = flatSegments.length;
  // Pair "X/Y complete" with the same ratio — weighted bar fill stays separate.
  const overallPercent =
    totalSegments > 0
      ? Math.round((completedCount / totalSegments) * 100)
      : 0;

  const runningLate = useMemo(() => {
    if (!scheduledEndTime || !expectedEndTime) return false;
    const scheduled = parseEndTimeToMinutes(scheduledEndTime);
    const expected = parseEndTimeToMinutes(expectedEndTime);
    if (scheduled === null || expected === null) return false;
    return expected > scheduled;
  }, [scheduledEndTime, expectedEndTime]);

  const initial =
    (displayName || "?").trim().charAt(0).toUpperCase() || "?";

  const labelStyle: React.CSSProperties = {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    margin: 0,
  };

  return (
    <header
      style={{
        position: "relative",
        display: "flex",
        flexDirection: compact ? "column" : "row",
        flexWrap: compact ? undefined : "wrap",
        alignItems: compact ? "stretch" : "center",
        columnGap: compact ? undefined : 12,
        rowGap: compact ? undefined : 8,
        gap: compact ? 8 : undefined,
        padding: compact ? "8px 10px" : "10px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        background: "#141820",
        flexShrink: 0,
        overflow: "visible",
        width: "100%",
        boxSizing: "border-box",
        zIndex: 30,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 8 : 12,
          minWidth: 0,
          width: "100%",
          flex: compact ? undefined : 1,
          flexWrap: compact ? undefined : "wrap",
        }}
      >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 8 : 12,
          minWidth: 0,
          flex: compact ? undefined : "0 1 auto",
          flexShrink: 0,
        }}
      >
        <Link
          to={homePath}
          replace
          title="Back to sessions"
          aria-label="Back to sessions"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: compact ? 30 : 32,
            height: compact ? 30 : 32,
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.04)",
            color: "#e2e8f0",
            textDecoration: "none",
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={compact ? 16 : 18} />
        </Link>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: "#fff",
              maxWidth: compact ? 160 : 280,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={sessionTitle}
          >
            {sessionTitle}
          </span>
        </div>
      </div>

      {!compact && totalSegments > 0 ? (
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: isTrainer ? 12 : 16,
            minWidth: 0,
            flex: "1 1 420px",
            overflow: "visible",
          }}
          role="region"
          aria-label="Session topic progress"
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              minWidth: 0,
              flex: "0 1 160px",
            }}
          >
            <Target size={14} color="#60a5fa" style={{ marginTop: 2 }} />
            <div style={{ minWidth: 0 }}>
              <p style={labelStyle}>Topic progress</p>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {completedCount}/{totalSegments} complete
                <span style={{ color: "#93c5fd" }}> · {overallPercent}%</span>
              </p>
            </div>
          </div>

          <TopicSegmentBar
            topics={topics}
            currentTopic={resolvedCurrent}
            topicProgressPercent={segmentProgressPercent}
            topicElapsedLabel={segmentElapsedLabel}
            teachingStarted={topicStarted}
            currentSubtopicId={currentSubtopicId}
            compact={!isTrainer}
          />

          {expectedEndTime || (isTrainer && scheduledEndTime) ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                flex: "0 0 auto",
                minWidth: 0,
                marginLeft: isTrainer ? 4 : 0,
                alignSelf: "center",
              }}
            >
              {isTrainer && scheduledEndTime ? (
                <div style={{ textAlign: "right" }}>
                  <p style={labelStyle}>Scheduled end</p>
                  <p
                    style={{
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#cbd5e1",
                    }}
                  >
                    <Calendar size={12} />
                    {scheduledEndTime}
                  </p>
                </div>
              ) : null}
              {expectedEndTime ? (
                <div style={{ textAlign: isTrainer ? "right" : "left" }}>
                  <p style={labelStyle}>Expected end</p>
                  <p
                    style={{
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: isTrainer ? "flex-end" : "flex-start",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      color: runningLate ? "#fcd34d" : "#6ee7b7",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Clock size={12} />
                    {expectedEndTime}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : compact ? null : (
        <div style={{ flex: 1, minWidth: 0 }} aria-hidden />
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: compact ? 8 : 10,
          flex: compact ? undefined : "1 1 200px",
          minWidth: 0,
          marginLeft: "auto",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: compact ? 6 : 10,
            color: "#94a3b8",
            fontSize: 12,
          }}
        >
          <button
            type="button"
            onClick={isTrainer ? onOpenAttendees : undefined}
            disabled={!isTrainer || !onOpenAttendees}
            title={
              isTrainer
                ? `${participantCount} in room — view attendees`
                : `${participantCount} in room`
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              margin: 0,
              padding: "4px 6px",
              border: "none",
              borderRadius: 8,
              background: "transparent",
              color: "inherit",
              cursor: isTrainer && onOpenAttendees ? "pointer" : "default",
              font: "inherit",
            }}
            onMouseEnter={(e) => {
              if (isTrainer && onOpenAttendees) {
                e.currentTarget.style.background = "rgba(148,163,184,0.12)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <Users size={14} />
            <span style={{ color: "#fff", fontWeight: 600 }}>
              {participantCount}
            </span>
          </button>
          {absenteeCount != null ? (
            <button
              type="button"
              onClick={isTrainer ? onOpenAbsentees : undefined}
              disabled={!isTrainer || !onOpenAbsentees}
              title={`${absenteeCount} absentee${
                absenteeCount === 1 ? "" : "s"
              } — view absentees`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                margin: 0,
                padding: "4px 6px",
                border: "none",
                borderRadius: 8,
                background: "transparent",
                color: "#f87171",
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: isTrainer && onOpenAbsentees ? "pointer" : "default",
                font: "inherit",
              }}
              onMouseEnter={(e) => {
                if (isTrainer && onOpenAbsentees) {
                  e.currentTarget.style.background = "rgba(248,113,113,0.12)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <UserX size={14} aria-hidden />
              {absenteeCount}
            </button>
          ) : null}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12,
            color: "#e2e8f0",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          <Clock size={14} color="#64748b" />
          {sessionTimer ?? "00:00:00"}
        </div>

        {isTrainer ? (
          <button
            type="button"
            onClick={onOpenAbsorption}
            title={`${doubtsCount} doubts · ${feedbackCount} feedback`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              border: "1px solid rgba(245,158,11,0.35)",
              borderRadius: 999,
              background: "rgba(245,158,11,0.15)",
              color: "#fde68a",
              fontSize: 10,
              fontWeight: 600,
              padding: compact ? "4px 8px" : "4px 10px",
              cursor: onOpenAbsorption ? "pointer" : "default",
              flexShrink: 1,
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            <CircleHelp size={12} style={{ flexShrink: 0 }} />
            {compact ? null : (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {doubtsCount} doubts · {feedbackCount} feedback
              </span>
            )}
          </button>
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color:
              connectionTone === "excellent"
                ? "#34d399"
                : connectionTone === "good"
                  ? "#86efac"
                  : connectionTone === "poor"
                    ? "#fbbf24"
                    : connectionTone === "lost" || connectionTone === "offline"
                      ? "#f87171"
                      : "#94a3b8",
            fontSize: 12,
            fontWeight: 600,
          }}
          title={connectionLabel}
        >
          <Wifi size={14} />
          {compact ? null : connectionLabel}
        </div>

        {isTrainer && (recording || recordingPaused) ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: recordingPaused ? "#fbbf24" : "#f87171",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: recordingPaused ? "#f59e0b" : "#ef4444",
              }}
            />
            {recordingPaused ? "PAUSED" : "REC"}
          </div>
        ) : null}

        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName || "User"}
            style={{
              width: compact ? 28 : 32,
              height: compact ? 28 : 32,
              borderRadius: "50%",
              objectFit: "cover",
              boxShadow: "0 0 0 2px rgba(255,255,255,0.2)",
            }}
          />
        ) : (
          <div
            style={{
              width: compact ? 28 : 32,
              height: compact ? 28 : 32,
              borderRadius: "50%",
              background: "#334155",
              color: "#e2e8f0",
              display: "grid",
              placeItems: "center",
              fontSize: 13,
              fontWeight: 700,
              boxShadow: "0 0 0 2px rgba(255,255,255,0.15)",
            }}
            title={displayName}
          >
            {initial}
          </div>
        )}
      </div>
      </div>

      {compact && totalSegments > 0 ? (
        <div
          role="region"
          aria-label="Session topic progress"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Target size={12} color="#60a5fa" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#fff",
                whiteSpace: "nowrap",
              }}
            >
              {completedCount}/{totalSegments}
              <span style={{ color: "#93c5fd" }}> · {overallPercent}%</span>
            </span>
          </div>
          <TopicSegmentBar
            topics={topics}
            currentTopic={resolvedCurrent}
            topicProgressPercent={segmentProgressPercent}
            topicElapsedLabel={segmentElapsedLabel}
            teachingStarted={topicStarted}
            currentSubtopicId={currentSubtopicId}
            compact
          />
          {expectedEndTime ? (
            <span
              style={{
                flexShrink: 0,
                fontSize: 10,
                fontWeight: 600,
                color: runningLate ? "#fcd34d" : "#6ee7b7",
                whiteSpace: "nowrap",
              }}
              title={`Expected end ${expectedEndTime}`}
            >
              {expectedEndTime}
            </span>
          ) : null}
        </div>
      ) : null}

      {mediaError ? (
        <div
          style={{
            position: "absolute",
            left: 16,
            right: 16,
            top: "100%",
            marginTop: 4,
            fontSize: 11,
            color: "#fbbf24",
            zIndex: 5,
            maxWidth: "min(420px, calc(100vw - 32px))",
            lineHeight: 1.35,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
          }}
          title={mediaError}
        >
          {mediaError}
        </div>
      ) : null}
    </header>
  );
}
